import * as Sentry from '@sentry/react';
import { Copy, Eye, EyeOff, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  DEVICE_FAMILY_REQUIRED_ERROR_CODE,
  DEVICE_PROFILE_NOT_READY_ERROR_CODE,
} from '../../constants/devices.js';
import {
  ANALOG_BANDWIDTH_OPTIONS,
  DEFAULT_ANALOG_MULTI_FREQUENCY_BANDWIDTH_MHZ,
  DEFAULT_DIGITAL_MULTI_FREQUENCY_BANDWIDTH_MHZ,
  DEFAULT_MANUAL_BANDWIDTH_MHZ,
  DEFAULT_MULTI_FREQUENCY_SIGNAL_TYPE,
  DIGITAL_BANDWIDTH_OPTIONS,
} from '../../constants/suppressions.js';
import {
  useDeleteDevice,
  useDeviceSettings,
  useFirmwareUpdateStatus,
  useIntl,
  useMe,
  useRebootDevice,
  useRoles,
  useUpdateDeviceSettings,
} from '../../hooks';
import { useLatestFirmwareForDevice, useTriggerFirmwareUpdate } from '../../hooks/useFirmwares.js';
import { formatDeviceModelDisplay } from '../../lib/device-model.js';
import { type ApiMutationError, getApiErrorCode } from '../../lib/error.js';
import { hzToMhz, mhzToHz } from '../../lib/frequency.js';
import { copyToClipboard } from '../../lib/utils';
import { isVersionLessThan } from '../../lib/version.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import { Device, DeviceStatus, DeviceType, SuppressionMode } from '../../types';
import { DeviceSettings, DeviceSettingsUpdate } from '../../types/device-settings.js';
import { SuppressionLogic } from '../../types/suppression-logic.js';
import { DeviceFamily } from '../../validations/device-form.js';
import {
  type DeviceSettingsFormValues,
  deviceSettingsSchema,
  MIN_DETECTION_LIFETIME_SECONDS,
  normalizeAlertPattern,
} from '../../validations/device-settings.js';
import { SignalTypeSelector } from '../devices/SignalTypeSelector.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  TagInput,
} from '../ui';
import { DeviceFamilyPicker } from './DeviceFamilyPicker.js';
import { DeviceLinkedDevicesManager } from './DeviceLinkedDevicesManager.js';
import { DeviceTagsManager } from './DeviceTagsManager.js';

type Props = {
  device: Device;
};

// A cleared field submits `null`, not `undefined`, and the difference is the whole bug: the settings
// patch is serialised with JSON.stringify, which DROPS an `undefined` key entirely, and the merge on
// the zahrada side is a spread — so an absent key cannot overwrite anything. A coordinate could be
// set but never removed.
//
// `null` is the established clear sentinel: the DTO accepts it, the numeric parser passes it through
// untouched for exactly this reason, and the azimuth toggle already sends it. Sending `''` is not an
// alternative — it is rejected with a 400, deliberately, because it used to be stored as 0.
//
// This runs when the field is READ at submit, not only when it is edited, so every rendered location
// input yields `null` while it is blank — including one nobody touched. Distinguishing "the operator
// emptied this" from "this was never filled in" is therefore not this helper's job; it is done at
// the payload with the form's dirty fields. See `TouchedLocationFields`.
//
// Only the four location fields register through this helper — azimuth, latitude, longitude,
// elevation — so the new meaning of a blank input is scoped to them. The `== null` guards on azimuth
// are loose, and match `null` as they did `undefined`.
const optionalNumberField = {
  setValueAs: (value: string) => (value === '' ? null : Number(value)),
};

const requiredNumberField = {
  setValueAs: (value: string) => Number(value),
};

// Number inputs, not requiredNumberField: Number('') is 0, which would save a cleared field as 0
// instead of letting the schema reject the blank. valueAsNumber reads it as NaN.
const numericInputField = { valueAsNumber: true } as const;

// Matches the bandwidth <select> styling in the create-suppression form (SuppressionForm.tsx)
// so the two default-bandwidth pickers here look consistent with it.
const bandwidthSelectClassName =
  'h-10 w-full rounded-md border border-border bg-background-light px-2 text-sm shadow-sm disabled:opacity-50';

/**
 * Whether the server knows the per-type multi-frequency widths at all. A zahrada that predates them
 * strips both keys from a settings write without an error (`whitelist: true`), so the two selects
 * would render, accept a choice, report success and snap back. They render only when a read carried
 * at least one of the pair.
 */
export const servesMultiFrequencyDefaults = (settings: Partial<DeviceSettings>): boolean =>
  settings.defaultAnalogMultiFrequencySuppressionBandwidth != null ||
  settings.defaultDigitalMultiFrequencySuppressionBandwidth != null;

/**
 * Its own gate rather than a third term on `servesMultiFrequencyDefaults`: the signal type reaches
 * zahrada later than the widths, so a server serving the widths and stripping the type is a live
 * combination, and folding the two together would render the type control against exactly that
 * server — the silent accept-and-snap-back the width gate exists to prevent.
 */
export const servesMultiFrequencySignalTypeDefault = (settings: Partial<DeviceSettings>): boolean =>
  settings.defaultMultiFrequencySuppressionSignalType != null;

/** A zahrada that predates alert patterns strips the key from a write without an error. */
export const servesAlertPatterns = (settings: Partial<DeviceSettings>): boolean =>
  Array.isArray(settings.alertPatterns);

export const toFormValues = (settings: Partial<DeviceSettings>): DeviceSettingsFormValues => {
  if (!settings) return {} as DeviceSettingsFormValues;

  return {
    // Absent keys stay absent. Seeding `null` instead looks tempting — it would let react-hook-form
    // mark a reverted field pristine — but RHF writes the default into the DOM, and a `null` in a
    // number input comes back as "0": a device with no coordinate would render, and save, at
    // latitude 0. That is the exact value this module already fought once.
    ...(settings.azimuth != null ? { azimuth: settings.azimuth } : {}),
    ...(settings.elevation != null ? { elevation: settings.elevation } : {}),
    ...(settings.latitude != null ? { latitude: settings.latitude } : {}),
    ...(settings.longitude != null ? { longitude: settings.longitude } : {}),
    // The four fields below do NOT follow "absent keys stay absent". That rule works for the
    // location inputs above because they are number inputs, where a seeded default would render as
    // "0"; these are <select>s that `deviceSettingsSchema` declares required, so an absent key does
    // not stay absent — react-hook-form falls back to the DOM and picks the first <option>, 2 MHz
    // in every one of these lists, which the operator then submits without ever choosing it.
    defaultAnalogSuppressionBandwidth:
      settings.defaultAnalogSuppressionBandwidth != null
        ? hzToMhz(settings.defaultAnalogSuppressionBandwidth)
        : DEFAULT_MANUAL_BANDWIDTH_MHZ,
    defaultDigitalSuppressionBandwidth:
      settings.defaultDigitalSuppressionBandwidth != null
        ? hzToMhz(settings.defaultDigitalSuppressionBandwidth)
        : DEFAULT_MANUAL_BANDWIDTH_MHZ,
    // The multi-frequency pair is the exception to the exception: its selects are not rendered when
    // the server serves neither key, so nothing falls back to the DOM and the keys stay absent —
    // and so the payload omits them instead of writing a width the operator never saw.
    ...(servesMultiFrequencyDefaults(settings)
      ? {
          defaultAnalogMultiFrequencySuppressionBandwidth:
            settings.defaultAnalogMultiFrequencySuppressionBandwidth != null
              ? hzToMhz(settings.defaultAnalogMultiFrequencySuppressionBandwidth)
              : DEFAULT_ANALOG_MULTI_FREQUENCY_BANDWIDTH_MHZ,
          defaultDigitalMultiFrequencySuppressionBandwidth:
            settings.defaultDigitalMultiFrequencySuppressionBandwidth != null
              ? hzToMhz(settings.defaultDigitalMultiFrequencySuppressionBandwidth)
              : DEFAULT_DIGITAL_MULTI_FREQUENCY_BANDWIDTH_MHZ,
        }
      : {}),
    ...(servesMultiFrequencySignalTypeDefault(settings)
      ? {
          defaultMultiFrequencySuppressionSignalType:
            settings.defaultMultiFrequencySuppressionSignalType,
        }
      : {}),
    defaultBroadbandSuppressionMode: settings.defaultBroadbandSuppressionMode ?? 'solid',
    deviceType: (settings.deviceType as DeviceType) ?? DeviceType.DETECTOR_SUPPRESSOR,
    detectionLifetime: Number(settings.detectionLifetime),
    friendOrFoeMaxCalculationTime: Number(settings.friendOrFoeMaxCalculationTime),
    friendOrFoeMinCalculationTime: Number(settings.friendOrFoeMinCalculationTime),
    maxAllowedFrequency: hzToMhz(Number(settings.maxAllowedFrequency)),
    maxSuppressionTime: Number(settings.maxSuppressionTime),
    minAllowedFrequency: hzToMhz(Number(settings.minAllowedFrequency)),
    multiChannelsSuppress: Number(settings.multiChannelsSuppress),
    suppressionAttemptDelay: Number(settings.suppressionAttemptDelay),
    suppressionInitialDelay: Number(settings.suppressionInitialDelay),
    suppressionLogic: settings.suppressionLogic as SuppressionLogic,
    suppressionMaxCount: Number(settings.suppressionMaxCount),
    suppressionMinDetectionsCount: Number(settings.suppressionMinDetectionsCount),
    suppressionMinSnr: Number(settings.suppressionMinSnr),
    suppressionMode: settings.suppressionMode as SuppressionMode,
    suppressionTime: Number(settings.suppressionTime),
    tags:
      settings.tags && Array.isArray(settings.tags)
        ? settings.tags
        : (settings?.tagIds ?? []).map((id) => ({ id, name: id })),
    txGainDecreaseLevel: Number(settings.txGainDecreaseLevel),
    jumperMode: settings.jumperMode ?? false,
    sapientEnabled: settings.sapientEnabled ?? false,
    alertPatterns: settings.alertPatterns ?? [],
  } as DeviceSettingsFormValues;
};

/**
 * Which location fields this session actually loaded a value for — the only ones it may clear.
 *
 * A `null` from a blank input means two different things: the operator emptied a field that held a
 * value, or the field was never populated at all. Sending `null` for the second is not a harmless
 * no-op — the API withholds the location fields from a non-admin's GET (they carry
 * `readPermission: 'admin'`) while accepting writes to them, so an operator's boxes render empty
 * for a device that does have coordinates, and any save would erase them.
 *
 * Keyed on "was a value loaded" rather than "did the operator edit it". Dirty state cannot carry
 * this: a blank field typed into and then emptied again stays dirty, because react-hook-form
 * compares against a default of `undefined` and `undefined !== null`. So a typo-and-undo in a box
 * whose value was withheld would still have sent a clear. A field that was never loaded can never
 * be cleared, whatever the operator does to it — and one that WAS loaded and is now blank was
 * emptied deliberately, which is the whole feature.
 */
export type ClearableLocationFields = Partial<
  Record<'latitude' | 'longitude' | 'elevation' | 'azimuth', boolean>
>;

export const buildDeviceSettingsPayload = (
  values: DeviceSettingsFormValues,
  includeLocation?: boolean,
  includeTags?: boolean,
  isAdmin?: boolean,
  clearAzimuth?: boolean,
  clearableLocation: ClearableLocationFields = {},
): DeviceSettingsUpdate => {
  const payload: DeviceSettingsUpdate = {
    deviceType: values.deviceType as DeviceType,
    defaultAnalogSuppressionBandwidth:
      values.defaultAnalogSuppressionBandwidth != null
        ? mhzToHz(values.defaultAnalogSuppressionBandwidth)
        : undefined,
    defaultDigitalSuppressionBandwidth:
      values.defaultDigitalSuppressionBandwidth != null
        ? mhzToHz(values.defaultDigitalSuppressionBandwidth)
        : undefined,
    defaultAnalogMultiFrequencySuppressionBandwidth:
      values.defaultAnalogMultiFrequencySuppressionBandwidth != null
        ? mhzToHz(values.defaultAnalogMultiFrequencySuppressionBandwidth)
        : undefined,
    defaultDigitalMultiFrequencySuppressionBandwidth:
      values.defaultDigitalMultiFrequencySuppressionBandwidth != null
        ? mhzToHz(values.defaultDigitalMultiFrequencySuppressionBandwidth)
        : undefined,
    defaultMultiFrequencySuppressionSignalType: values.defaultMultiFrequencySuppressionSignalType,
    defaultBroadbandSuppressionMode: values.defaultBroadbandSuppressionMode,
    detectionLifetime: values.detectionLifetime,
    friendOrFoeMaxCalculationTime: values.friendOrFoeMaxCalculationTime,
    friendOrFoeMinCalculationTime: values.friendOrFoeMinCalculationTime,
    maxAllowedFrequency: mhzToHz(values.maxAllowedFrequency),
    maxSuppressionTime: values.maxSuppressionTime,
    minAllowedFrequency: mhzToHz(values.minAllowedFrequency),
    multiChannelsSuppress: values.multiChannelsSuppress,
    suppressionAttemptDelay: values.suppressionAttemptDelay,
    suppressionInitialDelay: values.suppressionInitialDelay,
    suppressionLogic: values.suppressionLogic as SuppressionLogic,
    suppressionMaxCount: values.suppressionMaxCount,
    suppressionMinDetectionsCount: values.suppressionMinDetectionsCount,
    suppressionMinSnr: values.suppressionMinSnr,
    suppressionMode: values.suppressionMode as SuppressionMode,
    suppressionTime: values.suppressionTime,
    txGainDecreaseLevel: values.txGainDecreaseLevel,
    alertPatterns: values.alertPatterns,
  };

  if (isAdmin) {
    payload.jumperMode = values.jumperMode;
    payload.sapientEnabled = values.sapientEnabled;
  }

  if (includeLocation) {
    // A field carries into the payload when it has a value, or when it is blank AND this session
    // loaded a value for it — the second is what makes a deliberate clear reach the server. A blank
    // field whose value was never loaded is omitted, so it cannot erase what it was never shown.
    const sendLocation = (
      field: keyof ClearableLocationFields,
      value: number | null | undefined,
    ) => {
      if (value != null) payload[field] = value;
      else if (clearableLocation[field]) payload[field] = null;
    };

    // Azimuth keeps its own rule: the directional -> circular switch clears it as a consequence of
    // the mode change, which is not an edit of the field itself.
    if (clearAzimuth) payload.azimuth = null;
    else sendLocation('azimuth', values.azimuth);

    sendLocation('latitude', values.latitude);
    sendLocation('longitude', values.longitude);
    sendLocation('elevation', values.elevation);
  }

  if (includeTags) {
    const uniqueIds = Array.from(new Set((values.tags ?? []).map((tag) => tag.id)));
    payload.tagIds = uniqueIds;
  }

  return payload;
};

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const saveErrorMessage = (saveError: ApiMutationError, formatMessage: FormatMessage): string => {
  switch (getApiErrorCode(saveError)) {
    case DEVICE_FAMILY_REQUIRED_ERROR_CODE:
      return formatMessage({ id: 'deviceSettings.errors.familyRequired' });
    case DEVICE_PROFILE_NOT_READY_ERROR_CODE:
      return formatMessage({ id: 'deviceSettings.errors.profileNotReady' });
    default:
      return formatMessage({ id: 'deviceSettings.errors.saveFailed' });
  }
};

export default function DeviceSettingsPanel({ device }: Props) {
  const intl = useIntl();
  const updateSettings = useUpdateDeviceSettings();
  const deleteDeviceMutation = useDeleteDevice();
  const rebootMutation = useRebootDevice();
  const { data: me } = useMe();
  const { isAdminUser, isOperatorUser } = useRoles(me);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRebootDialogOpen, setIsRebootDialogOpen] = useState(false);
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const [saveError, setSaveError] = useState<ApiMutationError | null>(null);
  // Sticky, unlike `saveError`: a legacy row carries a raw model but no profile, so only the server
  // can say it needs a family, and clearing that on the next submit would unlock fields that can
  // only ever be rejected again.
  const [serverRequiresFamily, setServerRequiresFamily] = useState(false);
  const [familyAlreadySet, setFamilyAlreadySet] = useState(false);
  const familyRequired = device.model == null || serverRequiresFamily;
  const previousDeviceId = useRef(device.id);
  // Tracks whether the user has touched the directionality radio since the last
  // legitimate reset (device switch or post-save). When true, background refetches
  // must NOT overwrite sunflowerDirectional — the user's unsaved intent takes
  // precedence regardless of whether form fields are dirty (selecting "directional"
  // on a circular device doesn't dirty any form field until an azimuth is typed).
  const directionalityTouchedRef = useRef(false);
  const availableMultiChannelModes = [1, 2, 3];
  const isSunflower = device.model === DeviceFamily.SUNFLOWER;

  const {
    data: initialSettings,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useDeviceSettings(device.id);

  // A sunflower is directional when it currently has an azimuth set.
  const isInitiallyDirectional = (initialSettings as DeviceSettings)?.azimuth != null;
  const multiFrequencyDefaultsServed = servesMultiFrequencyDefaults(
    (initialSettings as DeviceSettings | null | undefined) ?? {},
  );
  const multiFrequencySignalTypeServed = servesMultiFrequencySignalTypeDefault(
    (initialSettings as DeviceSettings | null | undefined) ?? {},
  );
  const alertPatternsServed = servesAlertPatterns(
    (initialSettings as DeviceSettings | null | undefined) ?? {},
  );
  const [sunflowerDirectional, setSunflowerDirectional] = useState(isInitiallyDirectional);

  const { data: latestFirmware, isLoading: isLoadingFirmware } = useLatestFirmwareForDevice(
    device.id,
    { enabled: isAdminUser },
  );
  const triggerFirmwareUpdate = useTriggerFirmwareUpdate();
  const {
    status: firmwareUpdateStatus,
    clearStatus: clearFirmwareStatus,
    isInProgress: isFirmwareUpdating,
    isError: isFirmwareError,
  } = useFirmwareUpdateStatus(device.id);

  const initialFormValues = useMemo(
    () => toFormValues(initialSettings as DeviceSettings),
    [initialSettings],
  );
  const previousInitialFormValues = useRef<DeviceSettingsFormValues>(initialFormValues);

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm<DeviceSettingsFormValues>({
    resolver: zodResolver(deviceSettingsSchema),
    mode: 'onChange',
    defaultValues: initialFormValues,
  });

  // An <input type="number"> reports value === '' for content it cannot parse — a comma decimal
  // ("50,45", ordinary on a uk-UA keyboard), a lone '-', '1e', '12..3'. That is the same empty
  // string a genuinely cleared field produces, so without this a typo would read as a deliberate
  // clear and delete the coordinate on save, with no error shown.
  //
  // Tracked in component state rather than through setError: the form runs `mode: 'onChange'`, so
  // the zod resolver revalidates on every keystroke and would immediately clear an error set here —
  // the value it validates is `null`, which is perfectly valid.
  const [unparseableLocation, setUnparseableLocation] = useState<ClearableLocationFields>({});
  // The azimuth input is hidden on a circular sunflower (its wrapper takes `invisible`), and its
  // value is discarded there anyway. A stale flag on it would disable Save with nothing on screen
  // saying why, recoverable only by switching back to directional.
  const azimuthFieldHidden = isSunflower && !sunflowerDirectional;
  const hasUnparseableLocation = Object.entries(unparseableLocation).some(
    ([field, bad]) => bad && !(field === 'azimuth' && azimuthFieldHidden),
  );

  const locationField = (field: keyof ClearableLocationFields) => ({
    ...optionalNumberField,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const badInput = event.target.validity.badInput;
      setUnparseableLocation((previous) =>
        previous[field] === badInput ? previous : { ...previous, [field]: badInput },
      );
    },
  });

  const suppressionMode = watch('suppressionMode');
  const deviceType = watch('deviceType');
  const azimuth = watch('azimuth');

  // Ref to the azimuth <input> so switching to directional can focus + select it.
  const azimuthInputRef = useRef<HTMLInputElement | null>(null);
  const azimuthField = register('azimuth', locationField('azimuth'));

  // A directional sunflower needs an azimuth. Clearing the field now submits `null`, which does not
  // leave the device circular by being ignored — it actively erases the azimuth, so the save must
  // still be blocked. The reason changed; the guard did not.
  const directionalAzimuthMissing = isSunflower && sunflowerDirectional && azimuth == null;

  useEffect(() => {
    const isDeviceChanged = previousDeviceId.current !== device.id;
    const isSettingsChanged = previousInitialFormValues.current !== initialFormValues;
    previousDeviceId.current = device.id;
    previousInitialFormValues.current = initialFormValues;

    if (isDeviceChanged) {
      directionalityTouchedRef.current = false;
      setSaveError(null);
      setServerRequiresFamily(false);
      setFamilyAlreadySet(false);
      reset(initialFormValues);
      setSunflowerDirectional(isInitiallyDirectional);
      // The unparseable-input flags belong to the inputs being replaced. Left behind, a typo on one
      // device would block saving on the next one, showing an error for a field that is now fine.
      setUnparseableLocation({});
      return;
    }

    if (!isSettingsChanged) {
      return;
    }

    if (isDirty || directionalityTouchedRef.current) {
      // Preserve the user's unsaved edits across background settings refetches:
      // - isDirty covers form fields the user has typed into
      // - directionalityTouchedRef covers the radio toggle, which doesn't dirty the form
      //   until an azimuth value is typed (selecting "directional" on a circular device
      //   sets local state only, leaving form fields untouched)
      reset(initialFormValues, { keepDirtyValues: true });
    } else {
      setSunflowerDirectional(isInitiallyDirectional);
      reset(initialFormValues);
      // Same reasoning as the device switch: this branch replaces the field values wholesale. The
      // `keepDirtyValues` branch above deliberately does NOT clear them — there the operator's
      // unparseable text is still on screen, so the flag describing it is still true.
      setUnparseableLocation({});
    }
  }, [device.id, initialFormValues, isInitiallyDirectional, isDirty, reset]);

  useEffect(() => {
    if (deviceType === DeviceType.DETECTOR) {
      setValue('suppressionMode', SuppressionMode.DISABLED, { shouldValidate: true });
    }
  }, [deviceType, setValue]);

  // When the user switches a sunflower to directional, focus the azimuth field and
  // select its contents so the seeded default (or an existing value) can be typed
  // over immediately. Gated on the touched ref so opening a directional device does
  // not steal focus on mount.
  useEffect(() => {
    if (!sunflowerDirectional || !directionalityTouchedRef.current) return;
    const input = azimuthInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [sunflowerDirectional]);

  const onSubmit = handleSubmit(async (values) => {
    // A refetch that drops a key hides its control but `keepDirtyValues` keeps a dirty value in the
    // form, so each gate is applied to the values, not only to the rendering.
    const submittedValues = {
      ...values,
      ...(multiFrequencyDefaultsServed
        ? {}
        : {
            defaultAnalogMultiFrequencySuppressionBandwidth: undefined,
            defaultDigitalMultiFrequencySuppressionBandwidth: undefined,
          }),
      ...(multiFrequencySignalTypeServed
        ? {}
        : { defaultMultiFrequencySuppressionSignalType: undefined }),
      ...(alertPatternsServed ? {} : { alertPatterns: undefined }),
    };
    const payload = buildDeviceSettingsPayload(
      submittedValues,
      isAdminUser || isOperatorUser,
      isAdminUser,
      isAdminUser,
      // Admin only. `sunflowerDirectional` is seeded from `initialSettings.azimuth != null`, and the
      // API strips azimuth from a non-admin's GET — so an operator's radio ALWAYS reads "circular",
      // including for a directional device, and this would clear a real azimuth on any save.
      isAdminUser && isSunflower && !sunflowerDirectional,
      {
        latitude: initialFormValues.latitude != null,
        longitude: initialFormValues.longitude != null,
        elevation: initialFormValues.elevation != null,
        azimuth: initialFormValues.azimuth != null,
      },
    );
    if (await saveSettings(payload)) {
      directionalityTouchedRef.current = false;
      reset(values);
    }
  });

  // Tags are the one write zahrada accepts before a profile is bound. Skips `handleSubmit`: with no
  // schema the settings fields can leave the form invalid, which would block the save.
  const onSaveTagsOnly = async () => {
    const tagIds = Array.from(new Set((getValues('tags') ?? []).map((tag) => tag.id)));
    if (await saveSettings({ tagIds })) {
      reset({ ...initialFormValues, tags: getValues('tags') });
    }
  };

  const saveSettings = async (settings: DeviceSettingsUpdate): Promise<boolean> => {
    setSaveError(null);
    setFamilyAlreadySet(false);
    try {
      await updateSettings.mutateAsync({ id: device.id, settings });
      return true;
    } catch (error) {
      const rejection = error as ApiMutationError;
      setSaveError(rejection);
      if (rejection.code === DEVICE_FAMILY_REQUIRED_ERROR_CODE) setServerRequiresFamily(true);
      return false;
    }
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!familyRequired) return onSubmit(event);
    event.preventDefault();
    return onSaveTagsOnly();
  };

  const onFamilyAssigned = () => {
    setSaveError(null);
    setServerRequiresFamily(false);
  };

  const onFamilyAlreadyBound = () => {
    onFamilyAssigned();
    setFamilyAlreadySet(true);
  };

  const handleDelete = async () => {
    try {
      await deleteDeviceMutation.mutateAsync(device.id);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Failed to delete device:', error);
    }
  };

  const handleReboot = async () => {
    try {
      await rebootMutation.mutateAsync(device.id);
      setIsRebootDialogOpen(false);
    } catch (error) {
      console.error('Failed to reboot device:', error);
    }
  };

  if (isLoading && !initialSettings) {
    return (
      <div data-test-id="device-settings-panel" className="h-full flex flex-col">
        <Card className="border-0 shadow-none flex-1">
          <CardContent>
            <div className="text-sm text-foreground">
              {intl.formatMessage({ id: 'deviceSettings.loading' })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div data-test-id="device-settings-panel" className="h-full flex flex-col">
        <Card className="border-0 shadow-none flex-1">
          <CardHeader>
            <CardTitle>
              {intl.formatMessage({ id: 'deviceSettings.device' })} {device.serial}
            </CardTitle>
            <CardDescription className="text-destructive">
              {intl.formatMessage({ id: 'deviceSettings.loadError' })}:{' '}
              {error instanceof Error
                ? error.message
                : intl.formatMessage({ id: 'common.unknownError' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {intl.formatMessage({ id: 'common.retry' })}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const disableTagInputs = updateSettings.isPending || isFetching;
  const disableInputs = disableTagInputs || familyRequired;
  const disableSave = familyRequired
    ? !isAdminUser || updateSettings.isPending
    : disableInputs || !isValid || directionalAzimuthMissing || hasUnparseableLocation;

  return (
    <form
      data-test-id="device-settings-panel"
      onSubmit={onFormSubmit}
      className="h-full flex flex-col"
    >
      <div className="flex-1 overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>
                {device.serial} - {intl.formatMessage({ id: 'deviceSettings.title' })}
              </CardTitle>
              <CardDescription>
                {intl.formatMessage({ id: 'deviceSettings.description' })}
              </CardDescription>
              {isAdminUser && (
                <>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsTokenVisible(!isTokenVisible)}
                      className="h-6 p-0 hover:bg-background-light"
                      title={intl.formatMessage({
                        id: isTokenVisible ? 'deviceCard.hideToken' : 'deviceCard.showToken',
                      })}
                    >
                      {isTokenVisible ? (
                        <EyeOff className="h-3 w-3" />
                      ) : (
                        <Eye className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  {isTokenVisible && (
                    <div className="mt-2 p-2 bg-background-light rounded border border-border">
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-xs break-all">{device.token}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(device.token)}
                          className="h-6 w-6 p-0 hover:bg-background-light shrink-0"
                          title={intl.formatMessage({ id: 'deviceCard.copyToken' })}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-2">
              {device.status === DeviceStatus.ONLINE && (
                <Dialog open={isRebootDialogOpen} onOpenChange={setIsRebootDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      {intl.formatMessage({ id: 'deviceSettings.reboot' })}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {intl.formatMessage({ id: 'deviceSettings.reboot' })}
                      </DialogTitle>
                      <DialogDescription>
                        {intl.formatMessage({ id: 'deviceSettings.rebootConfirmation' })}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsRebootDialogOpen(false)}
                        disabled={rebootMutation.isPending}
                      >
                        {intl.formatMessage({ id: 'common.cancel' })}
                      </Button>
                      <Button onClick={handleReboot} disabled={rebootMutation.isPending}>
                        {intl.formatMessage({ id: 'deviceSettings.reboot' })}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {isAdminUser && (
                <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      {intl.formatMessage({ id: 'deviceSettings.unregisterDevice' })}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {intl.formatMessage({ id: 'deviceSettings.unregisterDevice' })}
                      </DialogTitle>
                      <DialogDescription>
                        {intl.formatMessage({ id: 'deviceSettings.unregisterConfirmation' })}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setIsDeleteDialogOpen(false)}
                        disabled={deleteDeviceMutation.isPending}
                      >
                        {intl.formatMessage({ id: 'common.cancel' })}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteDeviceMutation.isPending}
                      >
                        {deleteDeviceMutation.isPending
                          ? intl.formatMessage({ id: 'deviceSettings.unregistering' })
                          : intl.formatMessage({ id: 'deviceSettings.unregister' })}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Device Section */}
            <div className="space-y-4">
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.sections.device.title' })}
                </h3>
                <p className="text-xs text-foreground mt-1">
                  {intl.formatMessage({ id: 'deviceSettings.sections.device.description' })}
                </p>
              </div>
              <div className="grid gap-3">
                <div>
                  <Label className="text-sm font-medium">
                    {intl.formatMessage({ id: 'deviceSettings.fields.model' })}
                  </Label>
                  {isAdminUser && familyRequired ? (
                    <DeviceFamilyPicker
                      deviceId={device.id}
                      onAssigned={onFamilyAssigned}
                      onAlreadyBound={onFamilyAlreadyBound}
                    />
                  ) : (
                    <p
                      className="text-sm text-foreground mt-1"
                      data-test-id="device-settings-model-value"
                    >
                      {formatDeviceModelDisplay(device.model, device.revision) ||
                        intl.formatMessage({ id: 'deviceSettings.fields.modelUnknown' })}
                    </p>
                  )}
                  {familyAlreadySet && (
                    <p
                      role="status"
                      className="text-xs text-foreground mt-1"
                      data-test-id="device-family-already-set"
                    >
                      {intl.formatMessage({ id: 'deviceSettings.family.alreadySet' })}
                    </p>
                  )}
                  {!isAdminUser && familyRequired && (
                    <p className="text-xs text-foreground mt-1">
                      {intl.formatMessage({ id: 'deviceSettings.family.hintNonAdmin' })}
                    </p>
                  )}
                  {isAdminUser && familyRequired && (
                    <p
                      className="text-xs text-foreground mt-2"
                      data-test-id="device-settings-locked-hint"
                    >
                      {intl.formatMessage({ id: 'deviceSettings.family.settingsLocked' })}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">
                      {intl.formatMessage({ id: 'deviceSettings.fields.currentFirmwareVersion' })}
                    </Label>
                    <p className="text-sm text-foreground mt-1">
                      {device.firmwareVersion ||
                        intl.formatMessage({ id: 'deviceSettings.fields.firmwareVersionUnknown' })}
                    </p>
                  </div>
                  {isAdminUser &&
                    latestFirmware &&
                    !isLoadingFirmware &&
                    device.firmwareVersion &&
                    isVersionLessThan(device.firmwareVersion, latestFirmware.version) &&
                    device.status === DeviceStatus.ONLINE &&
                    !isFirmwareUpdating && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={triggerFirmwareUpdate.isPending}
                        onClick={() =>
                          triggerFirmwareUpdate.mutate({
                            firmwareId: latestFirmware.id,
                            deviceId: device.id,
                          })
                        }
                      >
                        {triggerFirmwareUpdate.isPending
                          ? intl.formatMessage({ id: 'deviceSettings.fields.firmwareUpdating' })
                          : intl.formatMessage(
                              { id: 'deviceSettings.fields.firmwareUpdateTo' },
                              { version: latestFirmware.version },
                            )}
                      </Button>
                    )}
                </div>
                {firmwareUpdateStatus && (
                  <div
                    className={`text-sm p-2 rounded flex items-center justify-between gap-2 ${
                      isFirmwareError
                        ? 'bg-destructive/10 text-destructive'
                        : isFirmwareUpdating
                          ? 'bg-accent/10 text-accent-foreground'
                          : 'bg-green-100 text-green-800'
                    }`}
                  >
                    <span>
                      {intl.formatMessage(
                        {
                          id: `deviceSettings.fields.firmwareStatus.${firmwareUpdateStatus.status}`,
                        },
                        {
                          version: firmwareUpdateStatus.firmwareVersion,
                          reason: firmwareUpdateStatus.reason || 'Unknown error',
                        },
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={clearFirmwareStatus}
                      className="shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
                      aria-label={intl.formatMessage({ id: 'common.close' })}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* General Settings Section */}
            <div className="space-y-4">
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.sections.general.title' })}
                </h3>
                <p className="text-xs text-foreground mt-1">
                  {intl.formatMessage({ id: 'deviceSettings.sections.general.description' })}
                </p>
              </div>

              <div className="grid gap-3">
                <Label>
                  {intl.formatMessage({ id: 'deviceSettings.fields.deviceType.label' })}
                </Label>
                <Controller
                  name="deviceType"
                  control={control}
                  render={({ field }) => (
                    <div className="flex gap-3">
                      {Object.values(DeviceType).map((type) => {
                        const isSelected = field.value === type;

                        return (
                          <label
                            className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                              isSelected
                                ? 'border-black bg-background-dark'
                                : 'border-border hover:bg-background-light'
                            } ${disableInputs ? 'cursor-not-allowed opacity-50' : ''}`}
                            key={`device-type-${type}`}
                          >
                            <input
                              type="radio"
                              value={type}
                              checked={isSelected}
                              onChange={(e) => field.onChange(e.target.value)}
                              disabled={disableInputs}
                              className="sr-only"
                            />
                            <div
                              className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                isSelected ? 'border-black bg-black' : 'border-border'
                              }`}
                            >
                              {isSelected && (
                                <div className="h-2 w-2 rounded-full bg-background-light" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">
                                {intl.formatMessage({
                                  id: `deviceSettings.fields.deviceType.${type}.label`,
                                })}
                              </div>
                              <div className="text-xs text-foreground">
                                {intl.formatMessage({
                                  id: `deviceSettings.fields.deviceType.${type}.description`,
                                })}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                />
                {errors.deviceType && (
                  <p className="text-sm text-destructive">{errors.deviceType.message}</p>
                )}
              </div>

              <div className="grid gap-3">
                <Label>
                  {intl.formatMessage({ id: 'deviceSettings.fields.suppressionMode.label' })}
                </Label>
                <Controller
                  name="suppressionMode"
                  control={control}
                  render={({ field }) => {
                    const availableModes =
                      deviceType === DeviceType.DETECTOR
                        ? [SuppressionMode.DISABLED]
                        : Object.values(SuppressionMode);

                    return (
                      <div className="flex gap-3">
                        {availableModes.map((mode) => {
                          const isSelected = field.value === mode;

                          return (
                            <label
                              className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                                isSelected
                                  ? 'border-black bg-background-dark'
                                  : 'border-border hover:bg-background-light'
                              } ${disableInputs ? 'cursor-not-allowed opacity-50' : ''}`}
                              key={`suppression-mode-${mode}`}
                            >
                              <input
                                type="radio"
                                value={mode}
                                checked={isSelected}
                                onChange={(e) => field.onChange(e.target.value)}
                                disabled={disableInputs}
                                className="sr-only"
                              />
                              <div
                                className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                  isSelected ? 'border-black bg-black' : 'border-border'
                                }`}
                              >
                                {isSelected && (
                                  <div className="h-2 w-2 rounded-full bg-background-light" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-sm">
                                  {intl.formatMessage({
                                    id: `deviceSettings.fields.suppressionMode.${mode}.label`,
                                  })}
                                </div>
                                <div className="text-xs text-foreground">
                                  {intl.formatMessage({
                                    id: `deviceSettings.fields.suppressionMode.${mode}.description`,
                                  })}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    );
                  }}
                />
                {errors.suppressionMode && (
                  <p className="text-sm text-destructive">{errors.suppressionMode.message}</p>
                )}
              </div>

              {suppressionMode !== SuppressionMode.DISABLED && (
                <>
                  <div className="grid gap-3">
                    <Label>
                      {intl.formatMessage({
                        id: 'deviceSettings.fields.multiChannelsSuppress.label',
                      })}
                    </Label>

                    <Controller
                      name="multiChannelsSuppress"
                      control={control}
                      render={({ field }) => {
                        return (
                          <div className="flex gap-3">
                            {availableMultiChannelModes.map((multiChannelMode) => {
                              const isSelected = field.value === multiChannelMode;

                              return (
                                <label
                                  className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                                    isSelected
                                      ? 'border-black bg-background-dark'
                                      : 'border-border hover:bg-background-light'
                                  } ${disableInputs ? 'cursor-not-allowed opacity-50' : ''}`}
                                  key={`multi-channels-suppress-${multiChannelMode}`}
                                >
                                  <input
                                    type="radio"
                                    value={multiChannelMode}
                                    checked={isSelected}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    disabled={disableInputs}
                                    className="sr-only"
                                  />
                                  <div
                                    className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                      isSelected ? 'border-black bg-black' : 'border-border'
                                    }`}
                                  >
                                    {isSelected && (
                                      <div className="h-2 w-2 rounded-full bg-background-light" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">
                                      {intl.formatMessage({
                                        id: `deviceSettings.fields.multiChannelsSuppress.${multiChannelMode}`,
                                      })}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    {errors.multiChannelsSuppress && (
                      <p className="text-sm text-destructive">
                        {errors.multiChannelsSuppress.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="suppressionTime">
                      {intl.formatMessage({ id: 'deviceSettings.fields.suppressionTime' })}
                    </Label>
                    <Input
                      id="suppressionTime"
                      type="number"
                      min={0}
                      disabled={disableInputs}
                      {...register('suppressionTime', numericInputField)}
                    />
                    {errors.suppressionTime && (
                      <p className="text-sm text-destructive">{errors.suppressionTime.message}</p>
                    )}
                  </div>

                  {isAdminUser && (
                    <div className="grid gap-2">
                      <Label htmlFor="maxSuppressionTime">
                        {intl.formatMessage({ id: 'deviceSettings.fields.maxSuppressionTime' })}
                      </Label>
                      <Input
                        id="maxSuppressionTime"
                        type="number"
                        min={1}
                        disabled={disableInputs}
                        {...register('maxSuppressionTime', numericInputField)}
                      />
                      {errors.maxSuppressionTime && (
                        <p className="text-sm text-destructive">
                          {errors.maxSuppressionTime.message}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="defaultAnalogSuppressionBandwidth">
                      {intl.formatMessage({
                        id: 'deviceSettings.fields.defaultAnalogSuppressionBandwidth',
                      })}
                    </Label>
                    <select
                      id="defaultAnalogSuppressionBandwidth"
                      disabled={disableInputs}
                      className={bandwidthSelectClassName}
                      {...register('defaultAnalogSuppressionBandwidth', requiredNumberField)}
                    >
                      {ANALOG_BANDWIDTH_OPTIONS.map((value) => (
                        <option key={`default-analog-bandwidth-${value}`} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                    {errors.defaultAnalogSuppressionBandwidth && (
                      <p className="text-sm text-destructive">
                        {errors.defaultAnalogSuppressionBandwidth.message}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="defaultDigitalSuppressionBandwidth">
                      {intl.formatMessage({
                        id: 'deviceSettings.fields.defaultDigitalSuppressionBandwidth',
                      })}
                    </Label>
                    <select
                      id="defaultDigitalSuppressionBandwidth"
                      disabled={disableInputs}
                      className={bandwidthSelectClassName}
                      {...register('defaultDigitalSuppressionBandwidth', requiredNumberField)}
                    >
                      {DIGITAL_BANDWIDTH_OPTIONS.map((value) => (
                        <option key={`default-digital-bandwidth-${value}`} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                    {errors.defaultDigitalSuppressionBandwidth && (
                      <p className="text-sm text-destructive">
                        {errors.defaultDigitalSuppressionBandwidth.message}
                      </p>
                    )}
                  </div>

                  {multiFrequencyDefaultsServed && (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="defaultAnalogMultiFrequencySuppressionBandwidth">
                          {intl.formatMessage({
                            id: 'deviceSettings.fields.defaultAnalogMultiFrequencySuppressionBandwidth',
                          })}
                        </Label>
                        <select
                          id="defaultAnalogMultiFrequencySuppressionBandwidth"
                          disabled={disableInputs}
                          className={bandwidthSelectClassName}
                          {...register(
                            'defaultAnalogMultiFrequencySuppressionBandwidth',
                            requiredNumberField,
                          )}
                        >
                          {ANALOG_BANDWIDTH_OPTIONS.map((value) => (
                            <option
                              key={`default-analog-multi-frequency-bandwidth-${value}`}
                              value={value}
                            >
                              {value}
                            </option>
                          ))}
                        </select>
                        {errors.defaultAnalogMultiFrequencySuppressionBandwidth && (
                          <p className="text-sm text-destructive">
                            {errors.defaultAnalogMultiFrequencySuppressionBandwidth.message}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="defaultDigitalMultiFrequencySuppressionBandwidth">
                          {intl.formatMessage({
                            id: 'deviceSettings.fields.defaultDigitalMultiFrequencySuppressionBandwidth',
                          })}
                        </Label>
                        <select
                          id="defaultDigitalMultiFrequencySuppressionBandwidth"
                          disabled={disableInputs}
                          className={bandwidthSelectClassName}
                          {...register(
                            'defaultDigitalMultiFrequencySuppressionBandwidth',
                            requiredNumberField,
                          )}
                        >
                          {DIGITAL_BANDWIDTH_OPTIONS.map((value) => (
                            <option
                              key={`default-digital-multi-frequency-bandwidth-${value}`}
                              value={value}
                            >
                              {value}
                            </option>
                          ))}
                        </select>
                        {errors.defaultDigitalMultiFrequencySuppressionBandwidth && (
                          <p className="text-sm text-destructive">
                            {errors.defaultDigitalMultiFrequencySuppressionBandwidth.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {multiFrequencySignalTypeServed && (
                    <div className="grid gap-2">
                      {/* No `htmlFor`: the control is a radio group, not a single labellable
                          element. `groupLabel` names it for assistive tech. */}
                      <Label>
                        {intl.formatMessage({
                          id: 'deviceSettings.fields.defaultMultiFrequencySuppressionSignalType',
                        })}
                      </Label>
                      <Controller
                        name="defaultMultiFrequencySuppressionSignalType"
                        control={control}
                        render={({ field }) => (
                          <SignalTypeSelector
                            groupLabel={intl.formatMessage({
                              id: 'deviceSettings.fields.defaultMultiFrequencySuppressionSignalType',
                            })}
                            name="defaultMultiFrequencySuppressionSignalType"
                            value={field.value ?? DEFAULT_MULTI_FREQUENCY_SIGNAL_TYPE}
                            disabled={disableInputs}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      {errors.defaultMultiFrequencySuppressionSignalType && (
                        <p className="text-sm text-destructive">
                          {errors.defaultMultiFrequencySuppressionSignalType.message}
                        </p>
                      )}
                    </div>
                  )}

                  {isSunflower && (
                    <div className="grid gap-3">
                      <Label>
                        {intl.formatMessage({
                          id: 'deviceSettings.fields.defaultBroadbandSuppressionMode.label',
                        })}
                      </Label>
                      <Controller
                        name="defaultBroadbandSuppressionMode"
                        control={control}
                        render={({ field }) => (
                          <div className="flex gap-3">
                            {(['solid', 'hopping'] as const).map((mode) => {
                              const isSelected = field.value === mode;

                              return (
                                <label
                                  className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                                    isSelected
                                      ? 'border-black bg-background-dark'
                                      : 'border-border hover:bg-background-light'
                                  } ${disableInputs ? 'cursor-not-allowed opacity-50' : ''}`}
                                  key={`default-broadband-suppression-mode-${mode}`}
                                >
                                  <input
                                    type="radio"
                                    name="defaultBroadbandSuppressionMode"
                                    value={mode}
                                    checked={isSelected}
                                    onChange={() => field.onChange(mode)}
                                    disabled={disableInputs}
                                    className="sr-only"
                                  />
                                  <div
                                    className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                      isSelected ? 'border-black bg-black' : 'border-border'
                                    }`}
                                  >
                                    {isSelected && (
                                      <div className="h-2 w-2 rounded-full bg-background-light" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">
                                      {intl.formatMessage({
                                        id: `deviceSettings.fields.defaultBroadbandSuppressionMode.${mode}.label`,
                                      })}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      />
                      {errors.defaultBroadbandSuppressionMode && (
                        <p className="text-sm text-destructive">
                          {errors.defaultBroadbandSuppressionMode.message}
                        </p>
                      )}
                    </div>
                  )}

                  {suppressionMode == SuppressionMode.AUTO && (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="suppressionMaxCount">
                          {intl.formatMessage({ id: 'deviceSettings.fields.suppressionMaxCount' })}
                        </Label>
                        <Input
                          id="suppressionMaxCount"
                          type="number"
                          min={0}
                          disabled={disableInputs}
                          {...register('suppressionMaxCount', numericInputField)}
                        />
                        {errors.suppressionMaxCount && (
                          <p className="text-sm text-destructive">
                            {errors.suppressionMaxCount.message}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="suppressionInitialDelay">
                          {intl.formatMessage({
                            id: 'deviceSettings.fields.suppressionInitialDelay',
                          })}
                        </Label>
                        <Input
                          id="suppressionInitialDelay"
                          type="number"
                          min={0}
                          disabled={disableInputs}
                          {...register('suppressionInitialDelay', numericInputField)}
                        />
                        {errors.suppressionInitialDelay && (
                          <p className="text-sm text-destructive">
                            {errors.suppressionInitialDelay.message}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="suppressionMinSnr">
                          {intl.formatMessage({ id: 'deviceSettings.fields.suppressionMinSnr' })}
                        </Label>
                        <Input
                          id="suppressionMinSnr"
                          type="number"
                          min={0}
                          disabled={disableInputs}
                          {...register('suppressionMinSnr', numericInputField)}
                        />
                        {errors.suppressionMinSnr && (
                          <p className="text-sm text-destructive">
                            {errors.suppressionMinSnr.message}
                          </p>
                        )}
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="suppressionAttemptDelay">
                          {intl.formatMessage({
                            id: 'deviceSettings.fields.suppressionAttemptDelay',
                          })}
                        </Label>
                        <Input
                          id="suppressionAttemptDelay"
                          type="number"
                          min={0}
                          disabled={disableInputs}
                          {...register('suppressionAttemptDelay', numericInputField)}
                        />
                        {errors.suppressionAttemptDelay && (
                          <p className="text-sm text-destructive">
                            {errors.suppressionAttemptDelay.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Friend or Foe Settings Section */}
            {suppressionMode === SuppressionMode.AUTO && (isAdminUser || isOperatorUser) && (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.sections.friendOrFoe.title' })}
                  </h3>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({ id: 'deviceSettings.sections.friendOrFoe.description' })}
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {isAdminUser && (
                    <div className="grid gap-2">
                      <Label htmlFor="friendOrFoeMinCalculationTime">
                        {intl.formatMessage({
                          id: 'deviceSettings.fields.friendOrFoeMinCalculationTime',
                        })}
                      </Label>
                      <Input
                        id="friendOrFoeMinCalculationTime"
                        type="number"
                        min={1}
                        max={3600}
                        disabled={disableInputs}
                        {...register('friendOrFoeMinCalculationTime', numericInputField)}
                      />
                      {errors.friendOrFoeMinCalculationTime && (
                        <p className="text-sm text-destructive">
                          {errors.friendOrFoeMinCalculationTime.message}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="friendOrFoeMaxCalculationTime">
                      {intl.formatMessage({
                        id: 'deviceSettings.fields.friendOrFoeMaxCalculationTime',
                      })}
                    </Label>
                    <Input
                      id="friendOrFoeMaxCalculationTime"
                      type="number"
                      min={1}
                      max={3600}
                      disabled={disableInputs}
                      {...register('friendOrFoeMaxCalculationTime', numericInputField)}
                    />
                    {errors.friendOrFoeMaxCalculationTime && (
                      <p className="text-sm text-destructive">
                        {errors.friendOrFoeMaxCalculationTime.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="suppressionMinDetectionsCount">
                    {intl.formatMessage({
                      id: 'deviceSettings.fields.suppressionMinDetectionsCount',
                    })}
                  </Label>
                  <Input
                    id="suppressionMinDetectionsCount"
                    type="number"
                    min={0}
                    disabled={disableInputs}
                    {...register('suppressionMinDetectionsCount', numericInputField)}
                  />
                  {errors.suppressionMinDetectionsCount && (
                    <p className="text-sm text-destructive">
                      {errors.suppressionMinDetectionsCount.message}
                    </p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="suppressionLogic">
                    {intl.formatMessage({ id: 'deviceSettings.fields.suppressionLogic.label' })}
                  </Label>
                  <Controller
                    name="suppressionLogic"
                    control={control}
                    render={({ field }) => {
                      return (
                        <select
                          name="suppressionLogic"
                          id="suppressionLogic"
                          disabled={disableInputs}
                          value={field.value ?? SuppressionLogic.ALL_DETECTIONS}
                          onChange={(e) => field.onChange(e.target.value as SuppressionLogic)}
                          className="w-full rounded-md border border-border bg-background-light px-3 py-2 text-sm shadow-sm disabled:opacity-50"
                        >
                          <option
                            key={SuppressionLogic.ALL_DETECTIONS}
                            value={SuppressionLogic.ALL_DETECTIONS}
                          >
                            {intl.formatMessage({
                              id: `deviceSettings.fields.suppressionLogic.values.${SuppressionLogic.ALL_DETECTIONS}`,
                            })}
                          </option>
                          <option
                            key={SuppressionLogic.APPROACHING_ONLY}
                            value={SuppressionLogic.APPROACHING_ONLY}
                          >
                            {intl.formatMessage({
                              id: `deviceSettings.fields.suppressionLogic.values.${SuppressionLogic.APPROACHING_ONLY}`,
                            })}
                          </option>
                          <option
                            key={SuppressionLogic.APPROACHING_AND_UNKNOWN}
                            value={SuppressionLogic.APPROACHING_AND_UNKNOWN}
                          >
                            {intl.formatMessage({
                              id: `deviceSettings.fields.suppressionLogic.values.${SuppressionLogic.APPROACHING_AND_UNKNOWN}`,
                            })}
                          </option>
                        </select>
                      );
                    }}
                  />
                  {errors.suppressionLogic && (
                    <p className="text-sm text-destructive">{errors.suppressionLogic.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Detection Settings Section */}
            <div className="space-y-4">
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.sections.detection.title' })}
                </h3>
                <p className="text-xs text-foreground mt-1">
                  {intl.formatMessage({ id: 'deviceSettings.sections.detection.description' })}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="detectionLifetime">
                  {intl.formatMessage({ id: 'deviceSettings.fields.detectionLifetime' })}
                </Label>
                <Input
                  id="detectionLifetime"
                  type="number"
                  min={MIN_DETECTION_LIFETIME_SECONDS}
                  disabled={disableInputs}
                  {...register('detectionLifetime', numericInputField)}
                />
                <p className="text-xs text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.fields.detectionLifetimeHelp' })}
                </p>
                {errors.detectionLifetime && (
                  <p className="text-sm text-destructive">{errors.detectionLifetime.message}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="minAllowedFrequency">
                    {intl.formatMessage({ id: 'deviceSettings.fields.minAllowedFrequency' })}
                  </Label>
                  <Input
                    id="minAllowedFrequency"
                    type="number"
                    step="0.01"
                    min={0}
                    disabled={disableInputs}
                    {...register('minAllowedFrequency', numericInputField)}
                  />
                  {errors.minAllowedFrequency && (
                    <p className="text-sm text-destructive">{errors.minAllowedFrequency.message}</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="maxAllowedFrequency">
                    {intl.formatMessage({ id: 'deviceSettings.fields.maxAllowedFrequency' })}
                  </Label>
                  <Input
                    id="maxAllowedFrequency"
                    type="number"
                    step="0.01"
                    min={0}
                    disabled={disableInputs}
                    {...register('maxAllowedFrequency', numericInputField)}
                  />
                  {errors.maxAllowedFrequency && (
                    <p className="text-sm text-destructive">{errors.maxAllowedFrequency.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Location & Orientation Section */}
            {isAdminUser || isOperatorUser ? (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.sections.location.title' })}
                  </h3>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({ id: 'deviceSettings.sections.location.description' })}
                  </p>
                </div>
                {isSunflower && (
                  <div className="grid gap-3">
                    <Label>
                      {intl.formatMessage({ id: 'deviceSettings.fields.directionality.label' })}
                    </Label>
                    <div className="flex gap-3">
                      {[
                        { value: 'circular', selected: !sunflowerDirectional },
                        { value: 'directional', selected: sunflowerDirectional },
                      ].map(({ value, selected }) => (
                        <label
                          className={`flex cursor-pointer select-none items-center gap-3 rounded-lg border px-4 py-3 transition-colors flex-1 ${
                            selected
                              ? 'border-black bg-background-dark'
                              : 'border-border hover:bg-background-light'
                          } ${disableInputs ? 'cursor-not-allowed opacity-50' : ''}`}
                          key={`directionality-${value}`}
                        >
                          <input
                            type="radio"
                            name="directionality"
                            value={value}
                            checked={selected}
                            onChange={() => {
                              directionalityTouchedRef.current = true;
                              if (value === 'directional') {
                                setSunflowerDirectional(true);
                                // Seed a usable default so the field is valid right away;
                                // the effect focuses + selects it for immediate overtyping.
                                if (azimuth == null) {
                                  setValue('azimuth', 0, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                }
                              } else {
                                setSunflowerDirectional(false);
                              }
                            }}
                            disabled={disableInputs}
                            className="sr-only"
                          />
                          <div
                            className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                              selected ? 'border-black bg-black' : 'border-border'
                            }`}
                          >
                            {selected && (
                              <div className="h-2 w-2 rounded-full bg-background-light" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {intl.formatMessage({
                                id: `deviceSettings.fields.directionality.${value}.label`,
                              })}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="latitude">
                      {intl.formatMessage({ id: 'deviceSettings.fields.latitude' })}
                    </Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      disabled={disableInputs}
                      {...register('latitude', locationField('latitude'))}
                    />
                    {(errors.latitude || unparseableLocation.latitude) && (
                      <p className="text-sm text-destructive">
                        {errors.latitude?.message ??
                          intl.formatMessage({ id: 'validation.deviceSettings.latitudeInvalid' })}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="longitude">
                      {intl.formatMessage({ id: 'deviceSettings.fields.longitude' })}
                    </Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      disabled={disableInputs}
                      {...register('longitude', locationField('longitude'))}
                    />
                    {(errors.longitude || unparseableLocation.longitude) && (
                      <p className="text-sm text-destructive">
                        {errors.longitude?.message ??
                          intl.formatMessage({ id: 'validation.deviceSettings.longitudeInvalid' })}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="elevation">
                      {intl.formatMessage({ id: 'deviceSettings.fields.elevation' })}
                    </Label>
                    <Input
                      id="elevation"
                      type="number"
                      step="any"
                      min={0}
                      disabled={disableInputs}
                      {...register('elevation', locationField('elevation'))}
                    />
                    {(errors.elevation || unparseableLocation.elevation) && (
                      <p className="text-sm text-destructive">
                        {errors.elevation?.message ??
                          intl.formatMessage({ id: 'validation.deviceSettings.elevationInvalid' })}
                      </p>
                    )}
                  </div>

                  {/* Azimuth is last so that hiding it in circular mode leaves no gap in the
                      grid. Always mounted (so the value round-trips across toggles and the
                      form never reflows); hidden via `invisible` when a sunflower is circular,
                      where a direction — and thus an azimuth — is meaningless. */}
                  <div
                    className={`grid gap-2 ${isSunflower && !sunflowerDirectional ? 'invisible' : ''}`}
                  >
                    <Label htmlFor="azimuth">
                      {intl.formatMessage({ id: 'deviceSettings.fields.azimuth' })}
                    </Label>
                    <Input
                      id="azimuth"
                      type="number"
                      step="any"
                      min={0}
                      max={359.99}
                      disabled={disableInputs}
                      {...azimuthField}
                      ref={(el) => {
                        azimuthField.ref(el);
                        azimuthInputRef.current = el;
                      }}
                    />
                    {(errors.azimuth || unparseableLocation.azimuth) && (
                      <p className="text-sm text-destructive">
                        {errors.azimuth?.message ??
                          intl.formatMessage({ id: 'validation.deviceSettings.azimuthInvalid' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-foreground">
                {intl.formatMessage({ id: 'deviceSettings.contactAdminForLocation' })}
              </p>
            )}

            <div className="space-y-4">
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.sections.testing.title' })}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {intl.formatMessage({ id: 'deviceSettings.sections.testing.description' })}
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="txGainDecreaseLevel">
                  {intl.formatMessage({ id: 'deviceSettings.fields.txGainDecreaseLevel' })}
                </Label>
                <Input
                  id="txGainDecreaseLevel"
                  type="number"
                  min={0}
                  disabled={disableInputs}
                  {...register('txGainDecreaseLevel', numericInputField)}
                />
                <p className="text-xs text-foreground">
                  {intl.formatMessage({ id: 'deviceSettings.fields.txGainDecreaseLevelHelp' })}
                </p>
                {errors.txGainDecreaseLevel && (
                  <p className="text-sm text-destructive">{errors.txGainDecreaseLevel.message}</p>
                )}
              </div>

              {isAdminUser && (
                <div className="grid gap-2">
                  <Controller
                    name="sapientEnabled"
                    control={control}
                    render={({ field }) => (
                      <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                          id="sapientEnabled"
                          type="checkbox"
                          checked={field.value ?? false}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={disableInputs}
                          className="h-4 w-4 cursor-pointer rounded border-border disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <span className="text-sm text-foreground">
                          {intl.formatMessage({ id: 'deviceSettings.fields.sapientEnabled' })}
                        </span>
                      </label>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Jumper Mode Section — admin only */}
            {isAdminUser && (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.fields.jumperMode' })}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {intl.formatMessage({ id: 'deviceSettings.fields.jumperModeHelp' })}
                  </p>
                </div>
                <Controller
                  name="jumperMode"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        id="jumperMode"
                        type="checkbox"
                        checked={field.value ?? false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={disableInputs}
                        className="h-4 w-4 cursor-pointer rounded border-border disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <span className="text-sm text-foreground">
                        {intl.formatMessage({ id: 'deviceSettings.fields.jumperMode' })}
                      </span>
                    </label>
                  )}
                />
              </div>
            )}

            {(isAdminUser || isOperatorUser) && alertPatternsServed ? (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.sections.alertPatterns.title' })}
                  </h3>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({
                      id: 'deviceSettings.sections.alertPatterns.description',
                    })}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Controller
                    name="alertPatterns"
                    control={control}
                    render={({ field }) => (
                      <TagInput
                        id="alertPatterns"
                        aria-label={intl.formatMessage({
                          id: 'deviceSettings.fields.alertPatterns',
                        })}
                        ref={field.ref}
                        name={field.name}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        normalizeTag={normalizeAlertPattern}
                        disabled={disableInputs}
                        invalid={!!errors.alertPatterns}
                        aria-describedby={errors.alertPatterns ? 'alertPatterns-error' : undefined}
                        placeholder={intl.formatMessage({
                          id: 'deviceSettings.alertPatterns.placeholder',
                        })}
                        removeLabel={(pattern) =>
                          intl.formatMessage(
                            { id: 'deviceSettings.alertPatterns.remove' },
                            { pattern },
                          )
                        }
                      />
                    )}
                  />
                  {errors.alertPatterns && (
                    <p id="alertPatterns-error" className="text-sm text-destructive">
                      {errors.alertPatterns.message}
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            {/* Tags Section */}
            {isAdminUser ? (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.sections.tags.title' })}
                  </h3>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({ id: 'deviceSettings.sections.tags.description' })}
                  </p>
                </div>
                <DeviceTagsManager control={control} disabled={disableTagInputs} />
              </div>
            ) : null}

            {/* Linked Devices Section */}
            {isAdminUser ? (
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="text-sm font-semibold text-foreground">
                    {intl.formatMessage({ id: 'deviceSettings.sections.linkedDevices.title' })}
                  </h3>
                  <p className="text-xs text-foreground mt-1">
                    {intl.formatMessage({
                      id: 'deviceSettings.sections.linkedDevices.description',
                    })}
                  </p>
                </div>
                <DeviceLinkedDevicesManager deviceId={device.id} disabled={disableTagInputs} />
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Fixed footer with Save button */}
      <div className="flex-shrink-0 border-t border-border bg-background-light p-6">
        {saveError && (
          <div
            key={saveError.code ?? 'generic'}
            role="alert"
            aria-live="assertive"
            data-test-id="device-settings-save-error"
            className="mb-3 rounded-md border border-destructive bg-destructive-light px-3 py-2 text-xs text-destructive-dark"
          >
            {saveErrorMessage(saveError, intl.formatMessage)}
          </div>
        )}
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={disableSave}>
            {updateSettings.isPending
              ? intl.formatMessage({ id: 'common.saving' })
              : intl.formatMessage({ id: 'common.save' })}
          </Button>
        </div>
      </div>
    </form>
  );
}
